/**
 * Multi-step Brew Skin creator / affiliate onboarding flow.
 */
class AffiliateFlow extends HTMLElement {
  #step = 0;
  #submitting = false;

  /** @type {{ creatorType: string; platforms: string[]; contentTypes: string[] }} */
  #answers = {
    creatorType: '',
    platforms: [],
    contentTypes: [],
  };

  connectedCallback() {
    this.#bind();
    this.#goTo(0);
  }

  #bind() {
    this.querySelectorAll('[data-affiliate-option]').forEach((btn) => {
      btn.addEventListener('click', () => this.#onOptionClick(/** @type {HTMLButtonElement} */ (btn)));
    });

    this.querySelectorAll('[data-affiliate-next]').forEach((btn) => {
      btn.addEventListener('click', () => this.#next());
    });

    this.querySelectorAll('[data-affiliate-back]').forEach((btn) => {
      btn.addEventListener('click', () => this.#back());
    });

    const form = this.querySelector('[data-affiliate-form]');
    if (form instanceof HTMLFormElement) {
      form.addEventListener('submit', (event) => {
        event.preventDefault();
        this.#submit(form);
      });
    }
  }

  /** @param {HTMLButtonElement} btn */
  #onOptionClick(btn) {
    const step = btn.closest('[data-affiliate-step]');
    if (!(step instanceof HTMLElement)) return;

    const mode = step.dataset.selectMode || 'single';
    const value = btn.dataset.affiliateOption || btn.textContent?.trim() || '';
    const group = step.dataset.answerKey || '';

    if (mode === 'multi') {
      btn.classList.toggle('is-selected');
      const selected = [...step.querySelectorAll('.affiliate-option.is-selected')].map(
        (el) => /** @type {HTMLElement} */ (el).dataset.affiliateOption || el.textContent?.trim() || ''
      );
      if (group === 'platforms') this.#answers.platforms = selected;
      if (group === 'contentTypes') this.#answers.contentTypes = selected;
    } else {
      step.querySelectorAll('.affiliate-option').forEach((el) => el.classList.remove('is-selected'));
      btn.classList.add('is-selected');
      if (group === 'creatorType') this.#answers.creatorType = value;
      if (group === 'platforms') this.#answers.platforms = [value];
      if (group === 'contentTypes') this.#answers.contentTypes = [value];
    }

    this.#clearError(step);
  }

  #steps() {
    return [...this.querySelectorAll('[data-affiliate-step]')];
  }

  /** @param {number} index */
  #goTo(index) {
    const steps = this.#steps();
    if (!steps.length) return;

    this.#step = Math.max(0, Math.min(index, steps.length - 1));

    steps.forEach((step, i) => {
      const active = i === this.#step;
      step.hidden = !active;
      step.classList.toggle('is-active', active);
      if (active) {
        step.classList.remove('is-enter');
        void step.offsetWidth;
        step.classList.add('is-enter');
      }
    });

    this.dataset.step = String(this.#step + 1);
    this.#updateProgress();
    this.#updateBackground();
  }

  #updateProgress() {
    const steps = this.#steps();
    this.querySelectorAll('[data-affiliate-dot]').forEach((dot, i) => {
      dot.classList.toggle('is-active', i === this.#step);
      dot.classList.toggle('is-done', i < this.#step);
      dot.setAttribute('aria-current', i === this.#step ? 'step' : 'false');
    });

    const label = this.querySelector('[data-affiliate-progress-label]');
    if (label) label.textContent = `Step ${this.#step + 1} of ${steps.length}`;
  }

  #updateBackground() {
    const step = this.#steps()[this.#step];
    if (!(step instanceof HTMLElement)) return;
    const bg = step.dataset.bg || '';
    if (bg) this.style.setProperty('--affiliate-step-bg', bg);
  }

  #next() {
    if (!this.#validateCurrent()) return;
    this.#goTo(this.#step + 1);
  }

  #back() {
    this.#goTo(this.#step - 1);
  }

  #validateCurrent() {
    const step = this.#steps()[this.#step];
    if (!(step instanceof HTMLElement)) return true;

    const key = step.dataset.answerKey;
    if (!key) return true;

    let ok = true;
    if (key === 'creatorType') ok = Boolean(this.#answers.creatorType);
    if (key === 'platforms') ok = this.#answers.platforms.length > 0;
    if (key === 'contentTypes') ok = this.#answers.contentTypes.length > 0;

    if (!ok) {
      this.#showError(step, step.dataset.errorMessage || 'Please select an option to continue.');
      return false;
    }

    this.#clearError(step);
    return true;
  }

  /** @param {HTMLElement} step @param {string} message */
  #showError(step, message) {
    let el = step.querySelector('[data-affiliate-error]');
    if (!(el instanceof HTMLElement)) {
      el = document.createElement('p');
      el.className = 'affiliate-error';
      el.setAttribute('data-affiliate-error', '');
      step.querySelector('.affiliate-card')?.appendChild(el);
    }
    el.textContent = message;
    el.hidden = false;
  }

  /** @param {HTMLElement} step */
  #clearError(step) {
    const el = step.querySelector('[data-affiliate-error]');
    if (el instanceof HTMLElement) el.hidden = true;
  }

  /** @param {HTMLFormElement} form */
  async #submit(form) {
    if (this.#submitting) return;
    if (!this.#validateCurrent()) return;

    if (!form.reportValidity()) return;

    this.#submitting = true;
    const submitBtn = form.querySelector('[type="submit"]');
    const original = submitBtn instanceof HTMLButtonElement ? submitBtn.textContent : '';
    if (submitBtn instanceof HTMLButtonElement) {
      submitBtn.disabled = true;
      submitBtn.textContent = submitBtn.dataset.loadingText || 'Submitting...';
    }

    const formData = new FormData(form);
    const payload = {
      creatorType: this.#answers.creatorType,
      platforms: this.#answers.platforms,
      platformsText: this.#answers.platforms.join(', '),
      contentTypes: this.#answers.contentTypes,
      contentTypesText: this.#answers.contentTypes.join(', '),
      fullName: String(formData.get('fullName') || ''),
      email: String(formData.get('email') || ''),
      instagram: String(formData.get('instagram') || ''),
      portfolio: String(formData.get('portfolio') || ''),
      submittedAt: new Date().toISOString(),
      page: window.location.href,
      shop: this.dataset.shop || '',
    };

    const bodyText = [
      'New Brew Skin Creator application',
      '',
      `Creator type: ${payload.creatorType}`,
      `Platform(s): ${payload.platforms.join(', ')}`,
      `Content: ${payload.contentTypes.join(', ')}`,
      '',
      `Name: ${payload.fullName}`,
      `Email: ${payload.email}`,
      `Instagram: ${payload.instagram || '—'}`,
      `Portfolio: ${payload.portfolio || '—'}`,
      '',
      `Submitted: ${payload.submittedAt}`,
      `Page: ${payload.page}`,
    ].join('\n');

    try {
      const contactForm = new FormData();
      contactForm.append('form_type', 'contact');
      contactForm.append('utf8', '✓');
      contactForm.append('contact[email]', payload.email);
      contactForm.append('contact[name]', payload.fullName);
      contactForm.append('contact[body]', bodyText);
      contactForm.append('contact[Creator type]', payload.creatorType);
      contactForm.append('contact[Platforms]', payload.platforms.join(', '));
      contactForm.append('contact[Content types]', payload.contentTypes.join(', '));
      contactForm.append('contact[Instagram]', payload.instagram);
      contactForm.append('contact[Portfolio]', payload.portfolio);

      const requests = [
        fetch('/contact', {
          method: 'POST',
          body: contactForm,
          headers: { Accept: 'application/json' },
        }),
      ];

      const webhook = this.dataset.webhook?.trim();
      if (webhook) {
        requests.push(
          fetch(webhook, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json',
            },
            body: JSON.stringify(payload),
            mode: 'cors',
          })
        );
      }

      const results = await Promise.allSettled(requests);
      const contactOk = results[0]?.status === 'fulfilled';
      const webhookOk = !webhook || results[1]?.status === 'fulfilled';

      if (!contactOk && !webhookOk) {
        throw new Error('Submission failed');
      }

      this.#showSuccess();
    } catch (error) {
      const step = this.#steps()[this.#step];
      if (step instanceof HTMLElement) {
        this.#showError(step, this.dataset.errorMessage || 'Something went wrong. Please try again.');
      }
      if (submitBtn instanceof HTMLButtonElement) {
        submitBtn.disabled = false;
        submitBtn.textContent = original;
      }
      this.#submitting = false;
      return;
    }

    if (submitBtn instanceof HTMLButtonElement) {
      submitBtn.disabled = false;
      submitBtn.textContent = original;
    }
    this.#submitting = false;
  }

  #showSuccess() {
    this.#steps().forEach((step) => {
      step.hidden = true;
      step.classList.remove('is-active');
    });

    const success = this.querySelector('[data-affiliate-success]');
    if (success instanceof HTMLElement) {
      success.hidden = false;
      success.classList.add('is-enter');
    }

    this.querySelector('[data-affiliate-progress]')?.setAttribute('hidden', '');
    this.dataset.step = 'done';
  }
}

if (!customElements.get('affiliate-flow')) {
  customElements.define('affiliate-flow', AffiliateFlow);
}
